/**
 * Inline create-prompt form rendered above the My-prompts list when the
 * "+ Prompt" CTA is open.
 *
 * Per Ean 2026-06-10 sharing-model refactor: creation is ALWAYS private to
 * the author. After save, the user can hit Share on the prompt's record
 * page to either (a) submit for admin review for workspace-wide distribution
 * or (b) share with specific teammates directly. This drops the binary
 * visibility picker at creation in favor of an explicit post-creation
 * sharing flow. Backend visibility stays 'user_private'; sharing creates
 * knowledge__prompt_assignment rows via the /share endpoint (which
 * uses an RLS carve-out so users can assign their own prompts to others).
 *
 * AI-assist wizard state per Ean 2026-06-10 (item 2 of punt-list close-out).
 * User describes what they want; we POST to /api/chat/stream with a
 * system-style preamble that asks the LLM to draft a reusable prompt
 * template. SSE response streams into the body textarea live.
 *
 * Note: /api/chat/stream is currently stubbed (the Anthropic API key isn't
 * yet provisioned at GCP Secret Manager path llm-olytic-managed-anthropic-haiku).
 * The wiring works end-to-end; the moment the key lands the wizard
 * produces real drafts without further code changes.
 */

import { Button } from '@olytic/foundation';
import { useRef, useState } from 'react';

import {
  createMyKnowledgePrompt,
  createChatSession,
  sendChatMessage,
  ApiError,
} from '../../lib/api.ts';
import { useToast } from '../../components/Toast.tsx';
import { ButtonCluster } from '../../components/ButtonCluster.tsx';

interface CreatePromptFormProps {
  /** Notify parent that a prompt saved successfully so it can reload + close. */
  onCreated: () => void | Promise<void>;
}

export function CreatePromptForm({ onCreated }: CreatePromptFormProps) {
  const { showToast } = useToast();

  const [createDraft, setCreateDraft] = useState<{
    name: string;
    description: string;
    body: string;
  }>({ name: '', description: '', body: '' });
  const [creating, setCreating] = useState(false);

  // AI assist widget collapse state per Ean 2026-06-10. Default is shown;
  // clicking "I'll write it myself" dismisses the whole card so the user
  // sees just the form. A small "Show AI assist" link reappears at the top
  // of the form for re-access. Local state — resets when the form is
  // re-opened (showCreateForm toggles back to true).
  const [assistDismissed, setAssistDismissed] = useState(false);
  const [assistMode, setAssistMode] = useState(false);
  const [assistDescription, setAssistDescription] = useState('');
  const [assistGenerating, setAssistGenerating] = useState(false);
  const assistAbort = useRef<AbortController | null>(null);

  async function handleGenerateDraft() {
    if (assistDescription.trim() === '') return;
    setAssistGenerating(true);
    // Clear the body textarea so the user sees the new draft replacing the old.
    setCreateDraft((d) => ({ ...d, body: '' }));
    try {
      // Step 1: create a one-off chat session using the user's default model
      // (no model_instance_id override — server picks the workspace default).
      const session = await createChatSession({});
      // Step 2: send a single message that prepends a system-style instruction
      // telling the model to draft a reusable prompt template (not a chat reply).
      const instruction =
        'You are a prompt-template architect. Given the description below, write a single reusable prompt template. ' +
        'Use {{variable_name}} placeholders for anything that would vary per use (recipient name, company, tone, etc.). ' +
        'Output ONLY the prompt body itself. no preamble, no markdown headers, no commentary, no "Here\'s your template:". ' +
        'Keep it concise but thorough enough that a teammate could drop it into chat and get useful output. ' +
        'Description: ' +
        assistDescription.trim();
      const ctl = new AbortController();
      assistAbort.current = ctl;
      const res = await sendChatMessage({
        session_id: session.id,
        message: instruction,
      });
      // Step 3: read the SSE stream. Each `data: {...}` frame may carry a
      // `delta` field with the next chunk of text. Accumulate into the body.
      const reader = res.body?.getReader();
      if (reader === null || reader === undefined) {
        throw new Error('Stream not readable.');
      }
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE frames split on double-newline.
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          if (!frame.startsWith('data: ')) continue;
          const payload = frame.slice(6).trim();
          if (payload === '' || payload === '[DONE]') continue;
          try {
            const parsed = JSON.parse(payload) as { delta?: string; text?: string; error?: string };
            if (parsed.error !== undefined) {
              throw new Error(parsed.error);
            }
            const chunk = parsed.delta ?? parsed.text ?? '';
            if (chunk !== '') {
              accumulated += chunk;
              setCreateDraft((d) => ({ ...d, body: accumulated }));
            }
          } catch {
            // Non-JSON frame — skip silently. Stub endpoint may emit prose.
          }
        }
      }
      if (accumulated === '') {
        showToast({
          kind: 'info',
          message:
            'Draft engine returned an empty response. The streaming pipeline is wired; the model key may not be active yet. Write the prompt manually for now.',
        });
      } else {
        showToast({ kind: 'success', message: 'Draft ready. Edit + save below.' });
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Could not draft prompt.',
      });
    } finally {
      setAssistGenerating(false);
      assistAbort.current = null;
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (createDraft.name.trim() === '' || createDraft.body.trim() === '') return;
    setCreating(true);
    try {
      await createMyKnowledgePrompt({
        name: createDraft.name.trim(),
        ...(createDraft.description.trim() !== '' ? { description: createDraft.description.trim() } : {}),
        body: createDraft.body,
        // Always private at creation per Ean 2026-06-10. Sharing happens on
        // the record page via the explicit Share flow, not at creation.
        visibility: 'user_private',
      });
      showToast({
        kind: 'success',
        message: 'Prompt saved. Open it from the list to share with teammates.',
      });
      setCreateDraft({ name: '', description: '', body: '' });
      setAssistDismissed(false);
      await onCreated();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not save prompt.',
      });
    } finally {
      setCreating(false);
    }
  }

  return (
    // User-side create-custom form per Ean 2026-06-09. Mirrors the admin
    // form shape but locked to user_private | user_shared visibilities.
    // Endpoint enforces the lock too — never trust the client.
    //
    // Setup fork per Ean 2026-06-09 (item 4b): two buttons sit at the top
    // of the form — "Set up with AI assist" (uses the user's assigned LLM
    // to draft the prompt from a description) and "I'll write it myself"
    // (manual).
    <form
      className="card card-padded"
      style={{ marginBottom: 'var(--card-gap)' }}
      onSubmit={handleCreate}
    >
      <h2 className="section-title" style={{ marginTop: 0 }}>
        Create your own prompt
      </h2>

      {/* Per Ean 2026-06-10: clicking "I'll write it myself" dismisses
         the whole AI assist card so the user just sees the form. A small
         "Show AI assist" link reappears in the title row so the user can
         bring it back if they change their mind. */}
      {!assistDismissed && (
        <div
          style={{
            margin: '0 0 16px',
            padding: '12px',
            background: 'var(--olytic-surface)',
            borderRadius: '8px',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: 'var(--olytic-obsidian)' }}>
                Need help drafting?
              </p>
              <p className="muted" style={{ margin: '2px 0 0', fontSize: '12px', lineHeight: 1.45 }}>
                Describe what you want and your assigned AI model will draft the prompt body with {'{{variable}}'} placeholders. Or dismiss this and write it yourself below.
              </p>
            </div>
            {/* Push 4-1g-hardaudit (2026-07-08): wrapped in ButtonCluster
               so the .btn-link Dismiss sibling shares the same 28px envelope
               as the primary/secondary .btn AI-assist toggle. Prior shape
               had a 0-border .btn-link floating next to a 1px-border .btn-sm.
               PLAT-308: the toggle is now a Foundation Button (brand while
               assist mode is on, outline otherwise). */}
            <ButtonCluster size="sm" className="button-cluster-flex-shrink-0">
              <Button
                type="button"
                variant={assistMode ? 'brand' : 'outline'}
                onClick={() => setAssistMode((v) => !v)}
                disabled={assistGenerating}
              >
                {assistMode ? 'Cancel AI assist' : 'Use AI assist'}
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={() => {
                  // Collapse the entire assist card + focus the form.
                  setAssistMode(false);
                  setAssistDismissed(true);
                  setTimeout(() => {
                    document.getElementById('prompt-create-name')?.focus();
                  }, 0);
                }}
              >
                Dismiss
              </Button>
            </ButtonCluster>
          </div>
          {assistMode && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <textarea
                className="form-input"
                rows={3}
                value={assistDescription}
                onChange={(e) => setAssistDescription(e.target.value)}
                placeholder="Describe what you want. e.g. 'A cold outreach email to a SaaS founder introducing my consulting practice, friendly but not casual, ~120 words.'"
                disabled={assistGenerating}
              />
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end' }}>
                <span className="muted" style={{ fontSize: '11.5px', marginRight: 'auto' }}>
                  {assistGenerating ? 'Drafting. Streams into the body below as it generates...' : 'Click Generate to draft. You can edit anything afterward.'}
                </span>
                <Button
                  type="button"
                  variant="brand"
                  size="sm"
                  onClick={handleGenerateDraft}
                  disabled={assistGenerating || assistDescription.trim() === ''}
                >
                  {assistGenerating ? 'Generating…' : 'Generate draft'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {assistDismissed && (
        <div style={{ margin: '0 0 12px', fontSize: '12.5px' }}>
          <Button type="button" variant="link" onClick={() => setAssistDismissed(false)}>
            ← Show AI assist
          </Button>
        </div>
      )}
      <p className="muted" style={{ margin: '4px 0 14px', fontSize: '12.5px', lineHeight: 1.45 }}>
        Saves to your private prompts. After save, open it from the list to share with specific teammates or submit for workspace-wide distribution.
      </p>
      <label className="form-field">
        <span>Name</span>
        <input
          type="text"
          id="prompt-create-name"
          className="form-input"
          value={createDraft.name}
          onChange={(e) => setCreateDraft({ ...createDraft, name: e.target.value })}
          required
          maxLength={120}
          placeholder="Draft cold outreach"
        />
      </label>
      <label className="form-field">
        <span>Description (optional)</span>
        <input
          type="text"
          className="form-input"
          value={createDraft.description}
          onChange={(e) => setCreateDraft({ ...createDraft, description: e.target.value })}
          maxLength={400}
          placeholder="What this is for, when to use it"
        />
      </label>
      <label className="form-field">
        <span>The prompt itself. Use {'{{variable_name}}'} for fill-ins.</span>
        <textarea
          className="form-input"
          rows={8}
          value={createDraft.body}
          onChange={(e) => setCreateDraft({ ...createDraft, body: e.target.value })}
          required
          placeholder="Write me a friendly outreach email to {{recipient_name}} introducing {{your_company}}..."
        />
      </label>
      {/* Visibility picker removed per Ean 2026-06-10 sharing-model refactor:
         saves as user_private always; sharing is an explicit post-creation
         action via the Share button on the prompt's record page. */}
      <div className="form-actions">
        <Button type="submit" variant="brand" disabled={creating}>
          {creating ? 'Saving' : 'Save prompt'}
        </Button>
      </div>
    </form>
  );
}
